import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  HostListener,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { NgbDropdown, NgbModule } from "@ng-bootstrap/ng-bootstrap";

import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexYAxis,
  ApexLegend,
  ApexFill,
  NgApexchartsModule,
} from "ng-apexcharts";
import { BackendApiService } from "../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../app/services/base-url-constants.service";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  colors: string[];
  legend: ApexLegend;
  fill: ApexFill;
  stroke: ApexStroke;
  grid: ApexGrid;
};

@Component({
  selector: "app-sync-charts-modal",
  standalone: true,
  templateUrl: "./sync-charts-modal.component.html",
  styleUrls: ["./sync-charts-modal.component.scss"],
  imports: [
    NgApexchartsModule,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    NgbModule,
  ],
})
export class SyncChartsModalComponent implements OnChanges, AfterViewInit {
  @ViewChild("chart") chart: ChartComponent;
  @Input() asset_id: string;
  @Input() device_id: string;
  @Input() client_id: string;
  @Input() subsystem_id: string;
  @Input() trendsFilter: "hour" | "day" | "week" = "day";
  @Output("closeStackedModal") closeStackedModal = new EventEmitter<boolean>();

  @HostListener("document:keydown.escape", ["$event"])
  handleEscapeKey(): void {
    this.onCancel();
  }

  isLoading = true;
  errorMessage = "";
  initialized = false;

  public cylinderBaseUrl = BaseUrlConstantsService.CYLINDER_BASE_URL;
  public chartOptionsMap: Record<string, ChartOptions> = {};

  public parameters = [
    {
      key: "vel",
      label: "Velocity",
      checked: false,
      metric_ids: [29, 30, 31],
      apiLabels: [],
    },
    {
      key: "temp",
      label: "Temperature",
      checked: false,
      metric_ids: [1, 2],
      apiLabels: ["Surface", "Cylinder End"],
    },
    {
      key: "ult",
      label: "Ultrasound",
      checked: false,
      metric_ids: [35],
      apiLabels: [],
    },
    {
      key: "flw",
      label: "Flow",
      checked: false,
      metric_ids: [],
      metric_name: "Flow",
      apiLabels: [],
    },
    {
      key: "aud",
      label: "Audible",
      checked: false,
      metric_ids: [36],
      apiLabels: [],
    },
    {
      key: "prr",
      label: "Pressure",
      checked: false,
      metric_ids: [],
      metric_name: "Pressure",
      apiLabels: [],
    },
  ];

  constructor(private srv: BackendApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.asset_id &&
      this.device_id &&
      this.client_id &&
      this.subsystem_id &&
      !this.initialized
    ) {
      this.initialized = true;
      this.parameters.forEach((p) => (p.checked = true));
      const url = this.buildURL();
      this.fetchAndUpdateChartData(url);
    }

    if (changes["trendsFilter"] && !changes["trendsFilter"].firstChange) {
      this.fetchAndUpdateChartData(this.buildURL());
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => window.dispatchEvent(new Event("resize")), 1500);
  }

  buildURL(): string {
    const selected = this.parameters.filter((p) => p.checked);
    const metricIds = selected.flatMap((p) => p.metric_ids).join(",");

    const flow = this.parameters.find((p) => p.label === "Flow")?.checked
      ? "1"
      : "0";
    const pressure = this.parameters.find((p) => p.label === "Pressure")
      ?.checked
      ? "1"
      : "0";

    // const baseUrl = `http://localhost:7778/cylinder/stacked-trends/${
    //   metricIds || null
    // }/${this.asset_id}/${this.device_id}/${this.client_id}/${
    //   this.subsystem_id
    // }/${this.trendsFilter}`;

    const baseUrl = `${this.cylinderBaseUrl}/cylinder/stacked-trends/${
      metricIds.length ? metricIds : null
    }/${this.asset_id}/${this.device_id}/${this.client_id}/${this.subsystem_id}/${
      this.trendsFilter
    }`;

    // const queryString = `?Flow=${flow}&Pressure=${pressure}`;

    return `${baseUrl}?Flow=${flow}&Pressure=${pressure}`;
  }

  fetchAndUpdateChartData(url: string): void {
    this.isLoading = true;
    this.srv
      .getDataV2(url)
      .then((res: any) => {
        if (!res.success || res.data.length === 0) {
          this.errorMessage = "No data available";
          return;
        }
        this.errorMessage = "";
        this.chartOptionsMap = {};

        const groupedSeries: Record<string, any[]> = {};
        let globalMin = Infinity;
        let globalMax = -Infinity;

        res.data.forEach((seriesObj: any) => {
          const matched = this.parameters.find((p) =>
            seriesObj.name.toLowerCase().includes(p.label.toLowerCase())
          );
          if (!matched) return;
          if (!groupedSeries[matched.key]) groupedSeries[matched.key] = [];

          const mapped = seriesObj.data.map(
            ([ts, val]: [string | number, number]) => {
              const t = this.roundToMinute(ts);
              if (t < globalMin) globalMin = t;
              if (t > globalMax) globalMax = t;
              return [t, val];
            }
          );

          groupedSeries[matched.key].push({
            name: seriesObj.name,
            data: mapped,
          });
        });

        Object.keys(groupedSeries).forEach((key) => {
          const colors = groupedSeries[key].map(() => this.getRandomColor());
          const firstName = groupedSeries[key][0].name;
          this.chartOptionsMap[key] = {
            ...this.getBaseChartOptions(key, firstName),
            colors,
            series: groupedSeries[key],
            xaxis: {
              tooltip: { enabled: false },
              type: "datetime",
              min: globalMin,
              max: globalMax,
              tickAmount: 6,
              labels: {
                show: true,
                rotate: -15,
                datetimeFormatter: {
                  year: "yyyy",
                  month: "MMM dd",
                  day: "dd MMM",
                },
                formatter: (val: string) =>
                  new Date(val).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
              },
            },
          };
        });

        setTimeout(() => window.dispatchEvent(new Event("resize")), 1500);
      })
      .catch((err) => {
        console.error("Chart data fetch error", err);
        this.errorMessage = "Error loading data";
      })
      .finally(() => (this.isLoading = false));
  }

  getBaseChartOptions(id: string, yAxisTitle: string): ChartOptions {
    return {
      chart: {
        id,
        group: "synced-charts",
        type: "area",
        height: 150,
        background: "#f5f7fa",
        toolbar: { show: true },
        zoom: { enabled: true, type: "x", autoScaleYaxis: true },
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      grid: { strokeDashArray: 4 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0.1 },
      },
      colors: ["#008FFB"],
      yaxis: {
        title: { text: yAxisTitle },
        labels: { formatter: (val: number) => val.toFixed(1) },
      },
      legend: { position: "top", horizontalAlign: "left" },
      series: [],
      xaxis: { type: "datetime" },
    };
  }

  applySelection(dropdown: NgbDropdown): void {
    this.fetchAndUpdateChartData(this.buildURL());
    dropdown.close();
  }

  handleTrendsFilter(filter: "hour" | "day" | "week"): void {
    this.trendsFilter = filter;
  }

  private getRandomColor(): string {
    return (
      "#" +
      Math.floor(Math.random() * 0xffffff)
        .toString(16)
        .padStart(6, "0")
    );
  }

  onCancel(): void {
    this.closeStackedModal.emit();
  }

  private roundToMinute(v: any): number {
    const d = new Date(v);
    d.setSeconds(0, 0);
    return d.getTime();
  }
}
